import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import AdjustIcon from '@mui/icons-material/Adjust'
import AlarmIcon from '@mui/icons-material/Alarm'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CancelIcon from '@mui/icons-material/Cancel'
import { UpTransition } from '../animations'
import { CompoundProductionStepType, PreparationLotDto, PreparationStatus } from '@services/dtos'
import { FormHeader } from '../forms/FormHeader'
import { useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { cancelStepTimer, startStepTimer, updatePreparationLot } from '@state/reducers/preparationLotReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import moment from 'moment'
import ConfirmDialog from './ConfirmDialog'
import { PreparationStatusLabels } from '@utils/constants'
import { useTimerCountdown, formatCountdown } from '@hooks/useTimerCountdown'
import { humanizeDurationSeconds } from '@utils/date.utils'

type Props = {
  open: boolean
  onClose: () => void
  preparationLot: PreparationLotDto | null
  readOnly?: boolean
}

export function PreparationLotStepsDialog({ open, onClose, preparationLot, readOnly }: Props) {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isStartingTimer, setIsStartingTimer] = useState(false)
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null)

  if (!preparationLot) {
    return null
  }

  const steps = preparationLot.compound.productionSteps ?? []
  const allDone = readOnly && [PreparationStatus.READY, PreparationStatus.RELEASED].includes(preparationLot.status)
  const currentIndex = allDone ? steps.length - 1 : (preparationLot.currentStepIndex ?? -1)
  const hasSteps = steps.length > 0
  const isLastStep = (index: number) => index === steps.length - 1

  const nextStep = steps[currentIndex + 1]
  const nextStepIsTimer = nextStep?.type === CompoundProductionStepType.TIMER

  const activeTimer = preparationLot.activeStepTimer ?? undefined
  const activeTimerForNextStep = activeTimer?.stepIndex === currentIndex + 1 ? activeTimer : undefined

  const handleAdvanceConfirmed = () => {
    if (pendingStepIndex === null) {
      return
    }
    const targetIndex = pendingStepIndex
    const isLast = isLastStep(targetIndex)
    setPendingStepIndex(null)
    setIsSubmitting(true)

    const dto = isLast
      ? { currentStepIndex: targetIndex, status: PreparationStatus.READY }
      : { currentStepIndex: targetIndex }
    dispatch(updatePreparationLot({ id: preparationLot.id, dto }))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({
          message: isLast ? 'Dernière étape validée, préparation prête' : 'Étape mise à jour',
          options: { variant: 'success' },
        }))
        if (isLast) {
          onClose()
        }
      })
      .catch(() => dispatch(enqueueSnackbar({ message: 'Erreur lors de la mise à jour', options: { variant: 'error' } })))
      .finally(() => setIsSubmitting(false))
  }

  const handleMarkReady = () => {
    setIsSubmitting(true)
    dispatch(updatePreparationLot({ id: preparationLot.id, dto: { status: PreparationStatus.READY } }))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'Préparation marquée comme prête', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() => dispatch(enqueueSnackbar({ message: 'Erreur lors de la mise à jour', options: { variant: 'error' } })))
      .finally(() => setIsSubmitting(false))
  }

  const handleCancelTimer = () => {
    if (!activeTimerForNextStep) {
      return
    }
    dispatch(cancelStepTimer({ preparationLotId: preparationLot.id, timerId: activeTimerForNextStep.id }))
      .unwrap()
      .catch(() => dispatch(enqueueSnackbar({ message: 'Erreur lors de l\'annulation du minuteur', options: { variant: 'error' } })))
  }

  const handleStartTimer = () => {
    if (!nextStep?.timerDurationSeconds) {
      return
    }
    setIsStartingTimer(true)
    dispatch(startStepTimer({ preparationLotId: preparationLot.id, dto: { stepIndex: currentIndex + 1, durationSeconds: nextStep.timerDurationSeconds } }))
      .unwrap()
      .catch(() => dispatch(enqueueSnackbar({ message: 'Erreur lors du lancement du minuteur', options: { variant: 'error' } })))
      .finally(() => setIsStartingTimer(false))
  }

  const pendingStep = pendingStepIndex !== null ? steps[pendingStepIndex] : null

  return (
    <>
      <Dialog open={open} onClose={onClose} TransitionComponent={UpTransition} maxWidth="sm" fullWidth>
        <DialogContent className="p-0">
          <div className="flex flex-col gap-4 p-6">
            <FormHeader
              title={`Étapes de production #${preparationLot.no}`}
              subTitles={[`${preparationLot.compound.name} - ${moment(preparationLot.preparedAt).format('DD/MM/YYYY HH:mm')}`]}
              onClose={onClose}
              isSubmitting={false}
              showSubmit={false}
            />

            {!hasSteps ? (
              <Alert severity="info">Aucune étape configurée pour ce MRP.</Alert>
            ) : (
              <List disablePadding>
                {steps.map((step, index) => {
                  const done = index <= currentIndex
                  const current = index === currentIndex + 1
                  const isTimerStep = step.type === CompoundProductionStepType.TIMER
                  const stepActiveTimer = isTimerStep && !readOnly && current ? activeTimer : undefined
                  const stepLog = preparationLot.stepLogs?.find(({ compoundProductionStep }) => compoundProductionStep?.id === step.id)
                  return (
                    <ListItem
                      key={step.id}
                      disablePadding
                      sx={{
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        bgcolor: !readOnly && current ? 'action.hover' : 'transparent',
                        cursor: !readOnly && index > currentIndex ? 'pointer' : 'default',
                        '&:hover': !readOnly && index > currentIndex ? { bgcolor: 'action.selected' } : {},
                      }}
                      onClick={() => {
                        if (!readOnly && index > currentIndex) {
                          setPendingStepIndex(index > currentIndex + 1 ? index - 1 : index)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {done ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : current ? (
                          <AdjustIcon color="primary" fontSize="small" />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              variant="body2"
                              fontWeight={current ? 600 : 400}
                              color={done ? 'text.secondary' : 'text.primary'}
                              sx={{ textDecoration: done && !readOnly ? 'line-through' : 'none' }}
                            >
                              {index + 1}. {step.label}
                            </Typography>
                            {step.type === CompoundProductionStepType.BOOLEAN ? (
                              <Typography variant="caption" color="text.secondary">({step.expected ? 'Oui' : 'Non'})</Typography>
                            ) : step.type === CompoundProductionStepType.DILUTION ? (
                              <Typography variant="caption" color="text.secondary">({step.dilutionVolumeMl} mL)</Typography>
                            ) : step.type === CompoundProductionStepType.TIMER ? (
                              <Typography variant="caption" color="text.secondary">({humanizeDurationSeconds(step.timerDurationSeconds)})</Typography>
                            ) : step.expectedValue ? (
                              <Typography variant="caption" color="text.secondary">({step.expectedValue}{step.unit ? ` ${step.unit}` : ''})</Typography>
                            ) : null}
                            {stepActiveTimer && (
                              <TimerChip timer={stepActiveTimer} />
                            )}
                            {!readOnly && index > currentIndex + 1 && (
                              <Chip label="Passer ici" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          stepLog ? (
                            <Typography variant="caption" color="text.secondary">
                              {`Validée par ${stepLog.createdBy.firstName} ${stepLog.createdBy.lastName} · ${moment(stepLog.createdAt).format('DD/MM HH:mm')}`}
                            </Typography>
                          ) : step.comment ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {step.comment}
                            </Typography>
                          ) : undefined
                        }
                      />
                    </ListItem>
                  )
                })}
              </List>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {!readOnly && hasSteps && currentIndex < steps.length - 1 && (
            nextStepIsTimer ? (
              <TimerActions
                timer={activeTimerForNextStep}
                isStartingTimer={isStartingTimer}
                isSubmitting={isSubmitting}
                onStart={handleStartTimer}
                onValidate={() => setPendingStepIndex(currentIndex + 1)}
                onCancel={handleCancelTimer}
              />
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={() => setPendingStepIndex(currentIndex + 1)}
              >
                Étape suivante
              </Button>
            )
          )}
          {!readOnly && (
            <Button
              variant="contained"
              color="success"
              disabled={isSubmitting || preparationLot.status === PreparationStatus.READY}
              onClick={handleMarkReady}
            >
              {hasSteps && !isLastStep(currentIndex)
                ? `Ignorer et marquer ${PreparationStatusLabels[PreparationStatus.READY]}`
                : `Marquer comme ${PreparationStatusLabels[PreparationStatus.READY]}`}
            </Button>
          )}
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={pendingStepIndex !== null}
        title="Valider l'étape"
        message={
          pendingStep
            ? isLastStep(pendingStepIndex!)
              ? `Valider "${pendingStep.label}" (dernière étape) ? La préparation passera automatiquement en ${PreparationStatusLabels[PreparationStatus.READY]}.`
              : `Valider l'étape "${pendingStep.label}" ?${pendingStepIndex! > currentIndex + 1 ? ' Les étapes intermédiaires seront ignorées.' : ''}`
            : ''
        }
        confirmButtonText="Valider"
        isProcessing={isSubmitting}
        onCancel={() => setPendingStepIndex(null)}
        onConfirm={handleAdvanceConfirmed}
      />
    </>
  )
}

function TimerChip({ timer }: { timer: { startedAt: string; durationSeconds: number } }) {
  const remaining = useTimerCountdown(timer.startedAt, timer.durationSeconds)
  if (remaining === null) {
    return null
  }
  if (remaining <= 0) {
    return <Chip icon={<AlarmIcon />} label="Terminé" color="warning" size="small" />
  }
  return (
    <Chip
      icon={<AlarmIcon />}
      label={formatCountdown(remaining)}
      color="primary"
      size="small"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    />
  )
}

type TimerActionsProps = {
  timer: { id: string; startedAt: string; durationSeconds: number } | undefined
  isStartingTimer: boolean
  isSubmitting: boolean
  onStart: () => void
  onValidate: () => void
  onCancel: () => void
}

function TimerActions({ timer, isStartingTimer, isSubmitting, onStart, onValidate, onCancel }: TimerActionsProps) {
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const remaining = useTimerCountdown(timer?.startedAt ?? null, timer?.durationSeconds ?? null)
  const timerFinished = remaining !== null && remaining <= 0

  const cancelButton = (
    <Tooltip title="Annuler le minuteur">
      <span>
        <IconButton size="small" color="error" disabled={isSubmitting} onClick={() => setConfirmCancelOpen(true)}>
          <CancelIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  )

  return (
    <>
      {!timer ? (
        <Button
          variant="contained"
          color="primary"
          startIcon={isStartingTimer ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          disabled={isStartingTimer || isSubmitting}
          onClick={onStart}
        >
          Lancer minuteur
        </Button>
      ) : timerFinished ? (
        <>
          {cancelButton}
          <Button variant="contained" color="success" disabled={isSubmitting} onClick={onValidate}>
            {'Valider l\'étape'}
          </Button>
        </>
      ) : (
        <Box display="flex" alignItems="center" gap={1}>
          <AlarmIcon color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {remaining !== null ? formatCountdown(remaining) : '...'}
          </Typography>
          {cancelButton}
        </Box>
      )}

      <ConfirmDialog
        open={confirmCancelOpen}
        title="Annulation minuteur"
        message="Êtes-vous sûr de vouloir annuler le minuteur en cours ?"
        onCancel={() => setConfirmCancelOpen(false)}
        onConfirm={() => { setConfirmCancelOpen(false); onCancel() }}
      />
    </>
  )
}
